//---React Imports
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
// import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMediaQuery } from "react-responsive";

//---Ant Design
import { Table, Card, Row, Col, Pagination, Tooltip } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";

//---Project Imports
import "./styles.css";
import { getHeartBeatHistory } from "apis/eolApis";
import { setNavStop } from "reducers/authSlice";
import {
  setHBHistoryData,
  setHBPageMini,
  setSelectedHBTestId,
  setHBStartDetails,
  setHBTestRunningStatus,
} from "reducers/eolTestsSlice";
import { formatShortDateAndTime } from "components/Common/Utils";
import Loading from "components/Common/Loading";
import NoData from "components/Common/NoData";
import APIError from "components/Common/APIError";

const HBHistoryPanel = () => {
  const dispatch = useDispatch();
  const eol = useSelector((state) => state.eolTests);
  const vehicleSlice = useSelector((state) => state.eol);
  const vIdVal = vehicleSlice?.vehicleDetails?.vehId;
  const HBHistoryData = eol?.HBHistoryData;
  const HBTestRunningStatus = eol?.HBTestRunningStatus;
  const page = eol?.HBPageMini;
  const pageSize = 4;
  const totalHBElements = eol?.totalHBElements;
  const isSmallScreen = useMediaQuery({ maxWidth: 1500 });
  const [selectedTestId, setSelectedTestId] = useState("");

  const {
    data: hbHistoryData,
    isLoading: hbHistoryIsLoading,
    isError: hbHistoryIsError,
    error: hbHistoryError,
  } = useQuery({
    queryKey: ["hbtests", vIdVal, page],
    queryFn: () => getHeartBeatHistory(vIdVal, page - 1, pageSize),
    keepPreviousData: true,
    enabled: !!vIdVal,
    staleTime: HBTestRunningStatus ? 0 : 1 * 10 * 60 * 1000, // If test is running cache is 10s, otherwise it is 10m
  });

  useEffect(() => {
    if (hbHistoryData?.content[0]?.status === "RUNNING") {
      dispatch(setHBStartDetails(hbHistoryData?.content[0]));
      dispatch(setHBTestRunningStatus(true));
      dispatch(setNavStop(true));
      dispatch(setSelectedHBTestId(hbHistoryData?.content[0]?.testId));
    }
    dispatch(
      setHBHistoryData({
        list: hbHistoryData?.content,
        total: hbHistoryData?.totalElements,
      })
    );
    // eslint-disable-next-line
  }, [hbHistoryData, dispatch]);

  useEffect(() => {
    dispatch(setNavStop(false));
  }, [hbHistoryError, dispatch]);

  const onColClick = (e, record) => {
    e.stopPropagation();
  };

  const onRowClick = async (e, record) => {
    if (HBTestRunningStatus) {
      e.preventDefault();
    } else {
      setSelectedTestId(record.testId);
      dispatch(setSelectedHBTestId(record.testId));
    }
  };

  const onPageChange = (page) => {
    dispatch(setHBPageMini(page));
  };

  const rowClassName = (record) => {
    return record.testId === selectedTestId ? "selected-row" : "";
  };
  const columns = [
    {
      title: "#",
      dataIndex: "iteration",
      key: "iteration",
    },
    {
      title: "Date",
      dataIndex: "testDate",
      key: "testDate",
      render: (testDate) =>
        isSmallScreen
          ? formatShortDateAndTime(testDate).substring(0, 10)
          : formatShortDateAndTime(testDate),
    },
  ];

  if (!isSmallScreen) {
    columns.push({
      // title: () => <InfoCircleOutlined />,
      title: "",
      dataIndex: "comment",
      key: "comment",
      render: (_, record) => (
        <Tooltip
          title={
            record?.comment?.length > 0
              ? record?.comment?.substring(0, 50)
              : "No Comment"
          }
          placement="right"
          color="#9e9f9f"
        >
          <EllipsisOutlined
            style={{ fontSize: "16px", cursor: "pointer" }}
            onMouseOver={(e) => onColClick(e, record)}
            onClick={(e) => onColClick(e, record)}
          />
        </Tooltip>
      ),
    });
  }

  return (
    <Card
      title="Previous Tests"
      className="h100"
      extra={
        <Pagination
          simple={{ readOnly: true }}
          showSizeChanger={false}
          current={page}
          pageSize={4}
          hideOnSinglePage
          total={totalHBElements}
          onChange={onPageChange}
          className="pagination-div"
          disabled={HBTestRunningStatus}
        />
      }
    >
      <>
        <Row>
          <Col span={24}>
            <div
              className={
                HBTestRunningStatus ? "history-panel-disabled" : "history-panel"
              }
            >
              {hbHistoryIsLoading && <Loading />}
              {hbHistoryIsError && (
                <APIError message={hbHistoryError.message} />
              )}
              {!hbHistoryIsLoading && !HBHistoryData?.length && <NoData />}
              {!hbHistoryIsError && HBHistoryData?.length > 0 && (
                <Table
                  columns={columns}
                  dataSource={HBHistoryData}
                  rowKey={(record) => record.iteration}
                  scroll={{ x: "100%" }}
                  onRow={(record) => ({
                    onClick: (e) => onRowClick(e, record),
                  })}
                  rowClassName={rowClassName}
                  rowHoverable={false}
                  pagination={false}
                  disabled={HBTestRunningStatus}
                />
              )}
            </div>
          </Col>
        </Row>
      </>
    </Card>
  );
};

export default HBHistoryPanel;
